import { useCallback, useEffect, useMemo } from 'react'
import AccountingToolPage from '@/components/AccountingToolPage'
import GcpAiRecommendations from '@/components/gcp/GcpAiRecommendations'
import GcpAnalyzerSection from '@/components/gcp/GcpAnalyzerSection'
import GcpBigQuerySection from '@/components/gcp/GcpBigQuerySection'
import GcpEgressSection from '@/components/gcp/GcpEgressSection'
import GcpMiscSection from '@/components/gcp/GcpMiscSection'
import {
  ANALYZER_STEPS,
  getSectionErrorMessage,
  getStepperRowStatus,
} from '@/components/gcp/gcpHelpers'
import GcpOverview from '@/components/gcp/GcpOverview'
import GcpWhatChanged from '@/components/gcp/GcpWhatChanged'
import GcpRunControls from '@/components/gcp/GcpRunControls'
import GcpServiceSection from '@/components/gcp/GcpServiceSection'
import GcpTopSpendTable from '@/components/gcp/GcpTopSpendTable'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  getAnalysisResultFromJob,
  getRecommendationsResultFromJob,
  startGcpCostAnalysis,
  startGcpCostRecommendations,
  WINDOW_PRESETS,
} from '@/services/gcpCosts'
import type { BackgroundJob } from '@/services/jobs'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { useGcpCostsStore } from '@/store/gcpCostsStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

function makePlaceholderJob(
  jobId: number,
  jobType: 'gcp_cost_analysis' | 'gcp_cost_recommendations',
  totalItems: number
): BackgroundJob {
  return {
    id: jobId,
    job_type: jobType,
    status: 'pending',
    total_items: totalItems,
    processed_items: 0,
    successful_items: 0,
    failed_items: 0,
    created_at: new Date().toISOString(),
  }
}

export default function GcpCosts() {
  const { canEdit } = usePageAccess('gcp')

  const windowPresetId = useGcpCostsStore((s) => s.windowPresetId)
  const metricsDays = useGcpCostsStore((s) => s.metricsDays)
  const analysisJobId = useGcpCostsStore((s) => s.analysisJobId)
  const recommendJobId = useGcpCostsStore((s) => s.recommendJobId)
  const analysisStatus = useGcpCostsStore((s) => s.analysisStatus)
  const recommendStatus = useGcpCostsStore((s) => s.recommendStatus)
  const analysisResult = useGcpCostsStore((s) => s.analysisResult)
  const recommendationsResult = useGcpCostsStore((s) => s.recommendationsResult)
  const analysisError = useGcpCostsStore((s) => s.analysisError)
  const recommendError = useGcpCostsStore((s) => s.recommendError)
  const lastRunAt = useGcpCostsStore((s) => s.lastRunAt)

  const setWindowPresetId = useGcpCostsStore((s) => s.setWindowPresetId)
  const setMetricsDays = useGcpCostsStore((s) => s.setMetricsDays)
  const setAnalysisJobId = useGcpCostsStore((s) => s.setAnalysisJobId)
  const setRecommendJobId = useGcpCostsStore((s) => s.setRecommendJobId)
  const setAnalysisStatus = useGcpCostsStore((s) => s.setAnalysisStatus)
  const setRecommendStatus = useGcpCostsStore((s) => s.setRecommendStatus)
  const setAnalysisResult = useGcpCostsStore((s) => s.setAnalysisResult)
  const setRecommendationsResult = useGcpCostsStore((s) => s.setRecommendationsResult)
  const setAnalysisError = useGcpCostsStore((s) => s.setAnalysisError)
  const setRecommendError = useGcpCostsStore((s) => s.setRecommendError)
  const setLastRunAt = useGcpCostsStore((s) => s.setLastRunAt)
  const resetForNewRun = useGcpCostsStore((s) => s.resetForNewRun)

  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const serverById = useBackgroundJobsStore((s) => s.serverById)

  const activeGlobalAnalysisJob = useMemo(() => {
    for (const { job } of Object.values(serverById)) {
      if (
        job.job_type === 'gcp_cost_analysis' &&
        (job.status === 'pending' || job.status === 'running')
      ) {
        return job
      }
    }
    return null
  }, [serverById])

  const analysisJob =
    (analysisJobId ? serverById[analysisJobId]?.job : null) ?? activeGlobalAnalysisJob
  const recommendJob = recommendJobId ? serverById[recommendJobId]?.job : null

  const isAnalysisRunning =
    analysisStatus === 'pending' ||
    analysisStatus === 'running' ||
    activeGlobalAnalysisJob != null
  const isRecommendRunning = recommendStatus === 'pending' || recommendStatus === 'running'

  useEffect(() => {
    if (analysisJobId != null || activeGlobalAnalysisJob == null) return
    setAnalysisJobId(activeGlobalAnalysisJob.id)
    if (
      activeGlobalAnalysisJob.status === 'pending' ||
      activeGlobalAnalysisJob.status === 'running'
    ) {
      setAnalysisStatus(activeGlobalAnalysisJob.status)
    }
  }, [
    activeGlobalAnalysisJob,
    analysisJobId,
    setAnalysisJobId,
    setAnalysisStatus,
  ])

  const handlePresetChange = useCallback(
    (presetId: string) => {
      const preset = WINDOW_PRESETS.find((p) => p.id === presetId)
      setWindowPresetId(presetId)
      if (preset) {
        setMetricsDays(preset.metricsDays)
      }
    },
    [setMetricsDays, setWindowPresetId]
  )

  const handleRunAnalysis = useCallback(async () => {
    if (!canEdit || isAnalysisRunning) return

    const preset = WINDOW_PRESETS.find((p) => p.id === windowPresetId) ?? WINDOW_PRESETS[2]!
    resetForNewRun()
    setAnalysisStatus('pending')
    setAnalysisError(null)

    try {
      const result = await startGcpCostAnalysis(preset.months, metricsDays)
      setAnalysisJobId(result.job_id)
      registerServerJob(
        makePlaceholderJob(result.job_id, 'gcp_cost_analysis', ANALYZER_STEPS.length)
      )
    } catch (err) {
      setAnalysisStatus('failed')
      setAnalysisError(formatApiError(err, 'Failed to start GCP cost analysis'))
      toast.error(formatApiError(err, 'Failed to start GCP cost analysis'))
    }
  }, [
    canEdit,
    isAnalysisRunning,
    metricsDays,
    registerServerJob,
    resetForNewRun,
    setAnalysisError,
    setAnalysisJobId,
    setAnalysisStatus,
    windowPresetId,
  ])

  const handleGenerateRecommendations = useCallback(async () => {
    if (!canEdit || !analysisJobId || !analysisResult || isRecommendRunning) return

    setRecommendStatus('pending')
    setRecommendError(null)

    try {
      const result = await startGcpCostRecommendations(analysisJobId)
      setRecommendJobId(result.job_id)
      registerServerJob(makePlaceholderJob(result.job_id, 'gcp_cost_recommendations', 1))
    } catch (err) {
      setRecommendStatus('failed')
      setRecommendError(formatApiError(err, 'Failed to start recommendations job'))
      toast.error(formatApiError(err, 'Failed to start recommendations job'))
    }
  }, [
    analysisJobId,
    analysisResult,
    canEdit,
    isRecommendRunning,
    registerServerJob,
    setRecommendError,
    setRecommendJobId,
    setRecommendStatus,
  ])

  useEffect(() => {
    if (!analysisJob) return

    if (analysisJob.status === 'pending' || analysisJob.status === 'running') {
      setAnalysisStatus(analysisJob.status)
      return
    }

    if (analysisJob.status === 'completed') {
      const result = getAnalysisResultFromJob(analysisJob)
      setAnalysisStatus('completed')
      setAnalysisResult(result)
      setAnalysisError(null)
      setLastRunAt(analysisJob.completed_at ?? new Date().toISOString())
      return
    }

    if (analysisJob.status === 'failed') {
      setAnalysisStatus('failed')
      setAnalysisError(analysisJob.error_message ?? 'Analysis job failed')
    }
  }, [
    analysisJob,
    setAnalysisError,
    setAnalysisResult,
    setAnalysisStatus,
    setLastRunAt,
  ])

  useEffect(() => {
    if (!recommendJob) return

    if (recommendJob.status === 'pending' || recommendJob.status === 'running') {
      setRecommendStatus(recommendJob.status)
      return
    }

    if (recommendJob.status === 'completed') {
      const result = getRecommendationsResultFromJob(recommendJob)
      setRecommendStatus('completed')
      setRecommendationsResult(result)
      setRecommendError(null)
      return
    }

    if (recommendJob.status === 'failed') {
      setRecommendStatus('failed')
      setRecommendError(recommendJob.error_message ?? 'Recommendations job failed')
    }
  }, [
    recommendJob,
    setRecommendError,
    setRecommendStatus,
    setRecommendationsResult,
  ])

  const processedItems = analysisJob?.processed_items ?? 0
  const showSections = analysisStatus !== 'idle'

  return (
    <AccountingToolPage
      title="GCP"
      description="Analyze blacksheep-408316 spending and find savings"
    >
      <div className="space-y-4">
        <GcpRunControls
          windowPresetId={windowPresetId}
          metricsDays={metricsDays}
          canEdit={canEdit}
          isRunning={isAnalysisRunning}
          isRecommendRunning={isRecommendRunning}
          hasResults={analysisResult != null}
          lastRunAt={lastRunAt}
          onPresetChange={handlePresetChange}
          onMetricsDaysChange={setMetricsDays}
          onRun={() => void handleRunAnalysis()}
          onGenerateRecommendations={() => void handleGenerateRecommendations()}
        />

        {analysisError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {analysisError}
          </div>
        ) : null}

        <GcpAiRecommendations
          analysis={analysisResult}
          analysisJobId={analysisJobId}
          recommendations={recommendationsResult}
          isRunning={isRecommendRunning}
          error={recommendError}
        />

        {showSections ? (
          <div className="space-y-3">
            {ANALYZER_STEPS.map((step, index) => {
              const rowStatus = getStepperRowStatus(
                index,
                processedItems,
                analysisStatus,
                analysisResult
              )
              const errorMessage = getSectionErrorMessage(analysisResult, step.key)
              const isDone = rowStatus === 'done'
              const defaultOpen = step.key === 'billing' && isDone

              return (
                <GcpAnalyzerSection
                  key={step.key}
                  id={`gcp-section-${step.key}`}
                  title={step.label}
                  status={rowStatus}
                  errorMessage={errorMessage}
                  defaultOpen={defaultOpen}
                >
                  {isDone && analysisResult && step.key === 'billing' ? (
                    <div className="space-y-6">
                      <GcpWhatChanged analysis={analysisResult} />
                      <GcpOverview analysis={analysisResult} />
                      <GcpTopSpendTable analysis={analysisResult} />
                    </div>
                  ) : null}
                  {isDone && analysisResult && step.key === 'bigquery' ? (
                    <GcpBigQuerySection analysis={analysisResult} />
                  ) : null}
                  {isDone && analysisResult && step.key === 'cloud_run' ? (
                    <GcpServiceSection
                      analysis={analysisResult}
                      sectionKey="cloud_run"
                      title="Cloud Run"
                      description="Per-service configuration, utilization, and cost drivers."
                    />
                  ) : null}
                  {isDone && analysisResult && step.key === 'cloud_sql' ? (
                    <GcpServiceSection
                      analysis={analysisResult}
                      sectionKey="cloud_sql"
                      title="Cloud SQL"
                      description="Instance sizing, HA settings, and utilization."
                    />
                  ) : null}
                  {isDone && analysisResult && step.key === 'gcs' ? (
                    <GcpServiceSection
                      analysis={analysisResult}
                      sectionKey="gcs"
                      title="Cloud Storage"
                      description="Buckets, storage classes, lifecycle rules, and egress patterns."
                    />
                  ) : null}
                  {isDone && analysisResult && step.key === 'compute' ? (
                    <GcpServiceSection
                      analysis={analysisResult}
                      sectionKey="compute"
                      title="Compute & Network"
                      description="Unused resources and network SKU costs — classic free-money findings."
                    />
                  ) : null}
                  {isDone && analysisResult && step.key === 'egress' ? (
                    <GcpEgressSection analysis={analysisResult} />
                  ) : null}
                  {isDone && analysisResult && step.key === 'misc' ? (
                    <GcpMiscSection analysis={analysisResult} />
                  ) : null}
                </GcpAnalyzerSection>
              )
            })}
          </div>
        ) : null}
      </div>
    </AccountingToolPage>
  )
}
